import React from 'react';
import {
  useAssignExpenseInvoiceBookings,
  useExpenseInvoicesListQuery,
  useLazyExpenseInvoiceQuery,
} from '../../ExpenseInvoices/Service/extendedExpenseInvoicesApi';
import { useBookingExpenseInvoiceSuggestionsQuery } from './extendedBookingsApi';
import { mapExpenseInvoiceDetailsToBookingExpenseInvoiceDetailsLookup } from './expenseInvoicesLookupMapper';
import type { BookingExpenseInvoiceDetailsLookup, BookingExpenseInvoiceLookup } from './expenseInvoicesLookupMapper';
import {
  type BookingExpenseInvoicesPagedResponse,
  createBookingExpenseInvoiceDetailsLookupTrigger,
  createSaveBookingExpenseInvoiceAssignmentsMutation,
  mapBookingExpenseInvoicesLookupData,
} from './expenseInvoicesLookupApi.contract';

type ExpenseInvoicesLookupQueryOptions = Parameters<typeof useExpenseInvoicesListQuery>[1];
type ExpenseInvoicesListQueryResult = ReturnType<typeof useExpenseInvoicesListQuery>;
type BookingExpenseInvoicesLookupQueryResult = Pick<
  ExpenseInvoicesListQueryResult,
  'isLoading' | 'isFetching' | 'isError' | 'error' | 'refetch'
> & {
  data: BookingExpenseInvoiceLookup[];
};
type ExpenseInvoiceDetailsLazyTriggerResult = ReturnType<
  ReturnType<typeof useLazyExpenseInvoiceQuery>[0]
>;
type BookingExpenseInvoiceDetailsLazyQueryState = Omit<
  ReturnType<typeof useLazyExpenseInvoiceQuery>[1],
  'data'
> & {
  data: BookingExpenseInvoiceDetailsLookup | undefined;
};
type BookingExpenseInvoiceDetailsLazyTrigger = (invoiceId: number) => Omit<
  ExpenseInvoiceDetailsLazyTriggerResult,
  'unwrap'
> & {
  unwrap: () => Promise<BookingExpenseInvoiceDetailsLookup>;
};

interface BookingExpenseInvoicesLookupArgs {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
}

export const useGetBookingExpenseInvoicesLookupQuery = (
  args: BookingExpenseInvoicesLookupArgs,
  options?: ExpenseInvoicesLookupQueryOptions
): BookingExpenseInvoicesLookupQueryResult => {
  const queryResult = useExpenseInvoicesListQuery(
    {
      pageNumber: args.pageNumber ?? 1,
      pageSize: args.pageSize ?? 50,
      ...(args.searchTerm ? { searchTerm: args.searchTerm } : {}),
    },
    options
  );
  const pagedResponse = queryResult.data as BookingExpenseInvoicesPagedResponse | undefined;

  const data = React.useMemo(() => mapBookingExpenseInvoicesLookupData(pagedResponse), [pagedResponse]);

  return {
    data,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useLazyGetBookingExpenseInvoiceDetailsLookupQuery = (): readonly [
  BookingExpenseInvoiceDetailsLazyTrigger,
  BookingExpenseInvoiceDetailsLazyQueryState,
] => {
  const [trigger, queryResult] = useLazyExpenseInvoiceQuery();

  const data = React.useMemo(
    () => mapExpenseInvoiceDetailsToBookingExpenseInvoiceDetailsLookup(queryResult.data),
    [queryResult.data]
  );

  const mappedTrigger = React.useMemo(
    () => createBookingExpenseInvoiceDetailsLookupTrigger(trigger),
    [trigger]
  );

  return [mappedTrigger, { ...queryResult, data }] as const;
};

export const useSaveBookingExpenseInvoiceAssignmentsMutation = () => {
  const [mutate, mutationState] = useAssignExpenseInvoiceBookings();

  const mappedMutation = React.useMemo(
    () => createSaveBookingExpenseInvoiceAssignmentsMutation(mutate),
    [mutate]
  );

  return [mappedMutation, mutationState] as const;
};

type ExpenseInvoiceSuggestionsForBookingQueryOptions = Parameters<
  typeof useBookingExpenseInvoiceSuggestionsQuery
>[1];

/**
 * Local adapter over `getExpenseInvoiceSuggestionsForBooking`. Isolates the panel
 * component from the underlying RTK Query slice (Single Change Surface).
 */
export const useGetBookingExpenseInvoiceSuggestionsLookupQuery = (
  bookingId: number,
  options?: ExpenseInvoiceSuggestionsForBookingQueryOptions
): ReturnType<typeof useBookingExpenseInvoiceSuggestionsQuery> =>
  useBookingExpenseInvoiceSuggestionsQuery(bookingId, options);
